/**
 * Generic App Bridge v1 — Observe and Advise only.
 * Two Darius capabilities: bounded locations summary, and model-advise (advisor /
 * document extract / grounded). A different contract or capability version is
 * unavailable; nothing is substituted.
 */

export const APP_BRIDGE_SCHEMA_VERSION = 'brunel.app-bridge.v1' as const;
export const APP_BRIDGE_CONTRACT_VERSION = '1.0.0' as const;
export const DARIUS_APP_KEY = 'darius' as const;
export const DARIUS_LOCATIONS_SUMMARY_CAPABILITY = 'darius.locations.summary' as const;
export const DARIUS_LOCATIONS_SUMMARY_VERSION = '1.0.0' as const;
export const DARIUS_AI_ADVISE_CAPABILITY = 'darius.ai.advise' as const;
export const DARIUS_AI_ADVISE_VERSION = '1.0.0' as const;

export const APP_BRIDGE_MODES = ['observe', 'advise'] as const;
export type AppBridgeMode = (typeof APP_BRIDGE_MODES)[number];

export const APP_BRIDGE_MODEL_ADVISE_KINDS = ['advisor', 'document_extract', 'grounded'] as const;
export type AppBridgeModelAdviseKind = (typeof APP_BRIDGE_MODEL_ADVISE_KINDS)[number];

export const APP_BRIDGE_RPC_NAMES = [
  'disconnect_app_connection',
  'grant_app_capability',
  'register_app_connection',
] as const;

/**
 * Doors a connected application reaches with its connection key and no sign-in. They are the
 * only functions in the API schema the unauthenticated role may execute, and the access
 * checks name them exactly: a new one is added here, never by loosening a check.
 */
export const APP_BRIDGE_CONNECTION_KEY_RPC_NAMES = [
  'app_key_disconnect_app_connection',
] as const;

export const APP_BRIDGE_FORBIDDEN_RPC_NAMES = [
  'mutate_app_capability',
  'mutate_darius',
  'operate_app_capability',
  'prepare_app_capability',
] as const;

export const INCOMPATIBLE_CONTRACT_VERSION = 'incompatible_contract_version' as const;
export const UNKNOWN_APPLICATION = 'unknown_application' as const;
export const UNKNOWN_CAPABILITY = 'unknown_capability' as const;
export const UNSUPPORTED_MODE = 'unsupported_mode' as const;

export type AppBridgeUnavailableReason =
  | typeof INCOMPATIBLE_CONTRACT_VERSION
  | typeof UNKNOWN_APPLICATION
  | typeof UNKNOWN_CAPABILITY
  | typeof UNSUPPORTED_MODE;

export interface AppBridgeLocationsCapability {
  readonly key: typeof DARIUS_LOCATIONS_SUMMARY_CAPABILITY;
  readonly version: typeof DARIUS_LOCATIONS_SUMMARY_VERSION;
  readonly modes: readonly AppBridgeMode[];
  readonly boundedRead: 'locations_summary';
}

export interface AppBridgeModelAdviseCapability {
  readonly key: typeof DARIUS_AI_ADVISE_CAPABILITY;
  readonly version: typeof DARIUS_AI_ADVISE_VERSION;
  readonly modes: readonly AppBridgeMode[];
  readonly boundedRead: 'model_advise';
  readonly kinds: readonly AppBridgeModelAdviseKind[];
}

export type AppBridgeCapability = AppBridgeLocationsCapability | AppBridgeModelAdviseCapability;

export interface AppBridgeApplication {
  readonly appKey: typeof DARIUS_APP_KEY;
  readonly tenantBinding: 'org';
  readonly capabilities: readonly [AppBridgeLocationsCapability, AppBridgeModelAdviseCapability];
}

export interface AppBridgeContract {
  readonly schemaVersion: typeof APP_BRIDGE_SCHEMA_VERSION;
  readonly contractVersion: typeof APP_BRIDGE_CONTRACT_VERSION;
  readonly noSilentFallback: true;
  readonly incompatibleContractVersion: {
    readonly available: false;
    readonly reason: typeof INCOMPATIBLE_CONTRACT_VERSION;
  };
  readonly applications: readonly [AppBridgeApplication];
}

export type AppBridgeAvailability =
  | {
    readonly available: true;
    readonly appKey: typeof DARIUS_APP_KEY;
    readonly capabilityKey: typeof DARIUS_LOCATIONS_SUMMARY_CAPABILITY | typeof DARIUS_AI_ADVISE_CAPABILITY;
    readonly capabilityVersion: '1.0.0';
    readonly contractVersion: typeof APP_BRIDGE_CONTRACT_VERSION;
    readonly modes: readonly AppBridgeMode[];
    readonly boundedRead: 'locations_summary' | 'model_advise';
  }
  | {
    readonly available: false;
    readonly reason: AppBridgeUnavailableReason;
  };

const LOCATIONS_CAPABILITY: AppBridgeLocationsCapability = {
  key: DARIUS_LOCATIONS_SUMMARY_CAPABILITY,
  version: DARIUS_LOCATIONS_SUMMARY_VERSION,
  modes: APP_BRIDGE_MODES,
  boundedRead: 'locations_summary',
};

const MODEL_ADVISE_CAPABILITY: AppBridgeModelAdviseCapability = {
  key: DARIUS_AI_ADVISE_CAPABILITY,
  version: DARIUS_AI_ADVISE_VERSION,
  modes: APP_BRIDGE_MODES,
  boundedRead: 'model_advise',
  kinds: APP_BRIDGE_MODEL_ADVISE_KINDS,
};

export const APP_BRIDGE_CONTRACT_V1: AppBridgeContract = {
  schemaVersion: APP_BRIDGE_SCHEMA_VERSION,
  contractVersion: APP_BRIDGE_CONTRACT_VERSION,
  noSilentFallback: true,
  incompatibleContractVersion: {
    available: false,
    reason: INCOMPATIBLE_CONTRACT_VERSION,
  },
  applications: [{
    appKey: DARIUS_APP_KEY,
    tenantBinding: 'org',
    capabilities: [LOCATIONS_CAPABILITY, MODEL_ADVISE_CAPABILITY],
  }],
};

export interface AppBridgeAssessmentRequest {
  readonly appKey: string;
  readonly capabilityKey: string;
  readonly capabilityVersion: string;
  readonly contractVersion: string;
  readonly mode: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sameKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const present = Object.keys(value);
  return present.length === keys.length && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function unavailable(reason: AppBridgeUnavailableReason): AppBridgeAvailability {
  return { available: false, reason };
}

function parseLocationsCapability(capability: Record<string, unknown>): AppBridgeLocationsCapability {
  if (!sameKeys(capability, ['key', 'version', 'modes', 'boundedRead'])) throw new Error('invalid_app_bridge_contract');
  if (capability.key !== DARIUS_LOCATIONS_SUMMARY_CAPABILITY || capability.version !== DARIUS_LOCATIONS_SUMMARY_VERSION) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (capability.boundedRead !== 'locations_summary' || !Array.isArray(capability.modes) || capability.modes.length !== APP_BRIDGE_MODES.length) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (capability.modes[0] !== 'observe' || capability.modes[1] !== 'advise') throw new Error('invalid_app_bridge_contract');
  return LOCATIONS_CAPABILITY;
}

function parseModelAdviseCapability(capability: Record<string, unknown>): AppBridgeModelAdviseCapability {
  if (!sameKeys(capability, ['key', 'version', 'modes', 'boundedRead', 'kinds'])) throw new Error('invalid_app_bridge_contract');
  if (capability.key !== DARIUS_AI_ADVISE_CAPABILITY || capability.version !== DARIUS_AI_ADVISE_VERSION) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (capability.boundedRead !== 'model_advise' || !Array.isArray(capability.modes) || capability.modes.length !== APP_BRIDGE_MODES.length) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (capability.modes[0] !== 'observe' || capability.modes[1] !== 'advise') throw new Error('invalid_app_bridge_contract');
  if (!Array.isArray(capability.kinds) || capability.kinds.length !== APP_BRIDGE_MODEL_ADVISE_KINDS.length) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (
    capability.kinds[0] !== 'advisor'
    || capability.kinds[1] !== 'document_extract'
    || capability.kinds[2] !== 'grounded'
  ) throw new Error('invalid_app_bridge_contract');
  return MODEL_ADVISE_CAPABILITY;
}

/** Strict v1 document. Wrong schema or contract version does not parse as this contract. */
export function parseAppBridgeContract(value: unknown): AppBridgeContract {
  if (!isRecord(value) || !sameKeys(value, [
    'schemaVersion', 'contractVersion', 'noSilentFallback', 'incompatibleContractVersion', 'applications',
  ])) throw new Error('invalid_app_bridge_contract');
  if (value.schemaVersion !== APP_BRIDGE_SCHEMA_VERSION || value.contractVersion !== APP_BRIDGE_CONTRACT_VERSION) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (value.noSilentFallback !== true) throw new Error('invalid_app_bridge_contract');
  if (!isRecord(value.incompatibleContractVersion) || !sameKeys(value.incompatibleContractVersion, ['available', 'reason'])) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (value.incompatibleContractVersion.available !== false || value.incompatibleContractVersion.reason !== INCOMPATIBLE_CONTRACT_VERSION) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (!Array.isArray(value.applications) || value.applications.length !== 1) throw new Error('invalid_app_bridge_contract');
  const application = value.applications[0];
  if (!isRecord(application) || !sameKeys(application, ['appKey', 'tenantBinding', 'capabilities'])) {
    throw new Error('invalid_app_bridge_contract');
  }
  if (application.appKey !== DARIUS_APP_KEY || application.tenantBinding !== 'org') throw new Error('invalid_app_bridge_contract');
  if (!Array.isArray(application.capabilities) || application.capabilities.length !== 2) throw new Error('invalid_app_bridge_contract');
  const first = application.capabilities[0];
  const second = application.capabilities[1];
  if (!isRecord(first) || !isRecord(second)) throw new Error('invalid_app_bridge_contract');
  parseLocationsCapability(first);
  parseModelAdviseCapability(second);
  return APP_BRIDGE_CONTRACT_V1;
}

/**
 * Resolve one requested capability. A mismatched schema, contract version, or
 * capability version is unavailable and is never replaced with 1.0.0.
 */
export function assessAppBridgeRequest(document: unknown, request: AppBridgeAssessmentRequest): AppBridgeAvailability {
  if (!isRecord(document)) return unavailable(INCOMPATIBLE_CONTRACT_VERSION);
  const schema = document.schemaVersion;
  const version = document.contractVersion;
  if (schema !== APP_BRIDGE_SCHEMA_VERSION || version !== APP_BRIDGE_CONTRACT_VERSION || request.contractVersion !== APP_BRIDGE_CONTRACT_VERSION) {
    return unavailable(INCOMPATIBLE_CONTRACT_VERSION);
  }
  const contract = parseAppBridgeContract(document);
  const application = contract.applications[0];
  if (request.appKey !== application.appKey) return unavailable(UNKNOWN_APPLICATION);
  const capability = application.capabilities.find((item) => item.key === request.capabilityKey);
  if (!capability) return unavailable(UNKNOWN_CAPABILITY);
  if (request.capabilityVersion !== capability.version) return unavailable(INCOMPATIBLE_CONTRACT_VERSION);
  if (request.mode !== 'observe' && request.mode !== 'advise') return unavailable(UNSUPPORTED_MODE);
  return {
    available: true,
    appKey: application.appKey,
    capabilityKey: capability.key,
    capabilityVersion: capability.version,
    contractVersion: contract.contractVersion,
    modes: capability.modes,
    boundedRead: capability.boundedRead,
  };
}
